import React, { useMemo } from 'react';
import { Calendar } from 'lucide-react';

const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const AvailabilitySlotForm = ({ formData, onChange, onSubmit, loading, partnerId, therapyFees, googleCalendarConnected = false }) => {
  /**
   * Generate next 4 weeks (28 days) including today
   * Format date in local timezone to avoid timezone conversion issues
   */
  const getNext4Weeks = () => {
    const days = [];
    for (let i = 0; i < 28; i++) {
      const date = new Date();
      date.setDate(date.getDate() + i);

      // Format date in local timezone (YYYY-MM-DD)
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const localDateString = `${year}-${month}-${day}`;

      days.push({
        value: localDateString,
        label: i === 0 ? 'Today' : date.toLocaleDateString('en-US', {
          weekday: 'short',
          month: 'short',
          day: 'numeric'
        })
      });
    }
    return days;
  };

  const next4Weeks = useMemo(() => getNext4Weeks(), []);

  // End date options: dates from date_start onward
  const endDateOptions = useMemo(() => {
    if (!formData.date_start) return next4Weeks;
    return next4Weeks.filter(d => d.value >= formData.date_start);
  }, [formData.date_start, next4Weeks]);

  // Auto-calculate end time based on selected therapy type's duration
  const calculatedEndTime = useMemo(() => {
    if (!formData.start_time || !formData.therapy_type || !therapyFees) return '';
    const selectedTherapy = therapyFees.find(t => t.therapy_type === formData.therapy_type);
    if (!selectedTherapy || !selectedTherapy.duration) return '';

    const [hours, minutes] = formData.start_time.split(':').map(Number);
    const totalMinutes = hours * 60 + minutes + selectedTherapy.duration;
    const endHours = String(Math.floor(totalMinutes / 60) % 24).padStart(2, '0');
    const endMinutes = String(totalMinutes % 60).padStart(2, '0');
    return `${endHours}:${endMinutes}`;
  }, [formData.start_time, formData.therapy_type, therapyFees]);

  const selectedTherapyData = useMemo(() => {
    if (!formData.therapy_type || !therapyFees) return null;
    return therapyFees.find(t => t.therapy_type === formData.therapy_type);
  }, [formData.therapy_type, therapyFees]);

  // Check if session mode requires Google Calendar
  const requiresGoogleCalendar = useMemo(() => {
    return formData.session_mode === 'available_offline_online' || formData.session_mode === 'available_online';
  }, [formData.session_mode]);

  // Check if Google Calendar is required but not connected
  const isGoogleCalendarRequired = useMemo(() => {
    return requiresGoogleCalendar && !googleCalendarConnected;
  }, [requiresGoogleCalendar, googleCalendarConnected]);

  const isRangeMode = formData.dateMode === 'range';

  // Check if all required fields are filled
  const isFormValid = useMemo(() => {
    const hasCommonFields = !!(
      formData.therapy_type &&
      formData.start_time &&
      formData.session_mode
    );

    let hasDateFields;
    if (isRangeMode) {
      hasDateFields = !!(
        formData.date_start &&
        formData.date_end &&
        formData.selected_days &&
        formData.selected_days.length > 0
      );
    } else {
      hasDateFields = !!formData.date;
    }

    const hasEndTime = !!calculatedEndTime;
    const hasGoogleCalendar = !isGoogleCalendarRequired;
    return hasCommonFields && hasDateFields && hasEndTime && hasGoogleCalendar;
  }, [formData.therapy_type, formData.date, formData.start_time, formData.session_mode,
      formData.date_start, formData.date_end, formData.selected_days, isRangeMode,
      calculatedEndTime, isGoogleCalendarRequired]);

  const handleSubmit = (e) => {
    e.preventDefault();

    if (isRangeMode) {
      if (!formData.date_start || !formData.date_end || !formData.start_time || !formData.therapy_type || !formData.session_mode) {
        alert('Please fill in all required fields');
        return;
      }
      if (!formData.selected_days || formData.selected_days.length === 0) {
        alert('Please select at least one day of the week');
        return;
      }
    } else {
      if (!formData.date || !formData.start_time || !formData.therapy_type || !formData.session_mode) {
        alert('Please fill in all required fields: Therapy Type, Date, Start Time, and Session Mode');
        return;
      }
    }

    if (!calculatedEndTime) {
      alert('Cannot calculate end time. Please ensure the therapy type has duration configured in Set Session Fee settings.');
      return;
    }

    onSubmit();
  };

  const handleDayToggle = (dayIndex) => {
    const currentDays = formData.selected_days || [];
    const newDays = currentDays.includes(dayIndex)
      ? currentDays.filter(d => d !== dayIndex)
      : [...currentDays, dayIndex].sort();
    onChange({ ...formData, selected_days: newDays });
  };

  const availableTherapyTypes = therapyFees && therapyFees.length > 0 ? therapyFees : [];

  return (
    <div className="bg-white dark:bg-dark-bg-secondary rounded-lg shadow-md p-6">
      <h3 className="text-lg font-semibold mb-4 flex items-center text-gray-900 dark:text-dark-text-primary">
        <Calendar className="h-5 w-5 mr-2 text-primary-600 dark:text-dark-primary-500" />
        Create Availability Slot
      </h3>

      <div className="mb-4 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-300 dark:border-amber-700 rounded-md">
        <p className="text-sm font-medium text-amber-700 dark:text-amber-400">
          Before creating Availability slots, you need to create Session Fee.
        </p>
      </div>

      <form onSubmit={handleSubmit}>
        {/* Date Mode Toggle */}
        <div className="mb-4">
          <div className="inline-flex rounded-md shadow-sm" role="group">
            <button
              type="button"
              onClick={() => onChange({ ...formData, dateMode: 'single' })}
              className={`px-4 py-2 text-sm font-medium rounded-l-md border ${
                !isRangeMode
                  ? 'bg-primary-600 text-white border-primary-600'
                  : 'bg-white dark:bg-dark-bg-secondary text-gray-700 dark:text-dark-text-secondary border-gray-300 dark:border-dark-border hover:bg-gray-50 dark:hover:bg-dark-bg-tertiary'
              }`}
            >
              Single Date
            </button>
            <button
              type="button"
              onClick={() => onChange({ ...formData, dateMode: 'range' })}
              className={`px-4 py-2 text-sm font-medium rounded-r-md border-t border-b border-r ${
                isRangeMode
                  ? 'bg-primary-600 text-white border-primary-600'
                  : 'bg-white dark:bg-dark-bg-secondary text-gray-700 dark:text-dark-text-secondary border-gray-300 dark:border-dark-border hover:bg-gray-50 dark:hover:bg-dark-bg-tertiary'
              }`}
            >
              Date Range
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Therapy Type Selector */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-2">
              Therapy Type <span className="text-red-500">*</span>
            </label>
            <select
              value={formData.therapy_type || ''}
              onChange={(e) => onChange({ ...formData, therapy_type: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-dark-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-dark-bg-secondary dark:text-dark-text-primary"
              required
            >
              <option value="">Select Therapy Type</option>
              {availableTherapyTypes.map(t => (
                <option key={t.therapy_type} value={t.therapy_type}>
                  {t.therapy_type.charAt(0).toUpperCase() + t.therapy_type.slice(1)} Therapy ({t.duration} min)
                </option>
              ))}
            </select>
          </div>

          {/* Date Selector - Single Mode */}
          {!isRangeMode && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-2">
                Date <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.date}
                onChange={(e) => onChange({ ...formData, date: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-dark-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-dark-bg-secondary dark:text-dark-text-primary"
                required
              >
                <option value="">Select Date</option>
                {next4Weeks.map(day => (
                  <option key={day.value} value={day.value}>
                    {day.label}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Date Range - Start Date */}
          {isRangeMode && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-2">
                Start Date <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.date_start || ''}
                onChange={(e) => {
                  const newStart = e.target.value;
                  const updates = { ...formData, date_start: newStart };
                  // Reset end date if it's before the new start date
                  if (formData.date_end && formData.date_end < newStart) {
                    updates.date_end = '';
                  }
                  onChange(updates);
                }}
                className="w-full px-3 py-2 border border-gray-300 dark:border-dark-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-dark-bg-secondary dark:text-dark-text-primary"
                required
              >
                <option value="">Select Start Date</option>
                {next4Weeks.map(day => (
                  <option key={day.value} value={day.value}>
                    {day.label}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Date Range - End Date */}
          {isRangeMode && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-2">
                End Date <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.date_end || ''}
                onChange={(e) => onChange({ ...formData, date_end: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-dark-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-dark-bg-secondary dark:text-dark-text-primary"
                required
              >
                <option value="">Select End Date</option>
                {endDateOptions.map(day => (
                  <option key={day.value} value={day.value}>
                    {day.label}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Start Time */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-2">
            Start Time <span className="text-red-500">*</span>
          </label>
            <input
              type="time"
              value={formData.start_time}
              onChange={(e) => onChange({ ...formData, start_time: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-dark-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-dark-bg-secondary dark:text-dark-text-primary"
              required
            />
          </div>

          {/* End Time (auto-calculated, read-only) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-2">
              End Time (auto-calculated)
            </label>
            <input
              type="time"
              value={calculatedEndTime}
              readOnly
              className="w-full px-3 py-2 border border-gray-200 dark:border-dark-border rounded-md bg-gray-50 dark:bg-dark-bg-tertiary text-gray-600 dark:text-dark-text-secondary cursor-not-allowed"
            />
            {selectedTherapyData && (
              <p className="bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 text-xs px-2 py-0.5 rounded mt-1">
                Duration: {selectedTherapyData.duration} minutes
              </p>
            )}
            {formData.therapy_type && formData.start_time && !calculatedEndTime && (
              <p className="bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200 text-xs px-2 py-0.5 rounded mt-1">
                Error: Unable to calculate end time. Please check Set Session Fee settings.
              </p>
            )}
          </div>

          {/* Session Mode */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-2">
              Session Mode <span className="text-red-500">*</span>
            </label>
            <select
              value={formData.session_mode || ''}
              onChange={(e) => onChange({ ...formData, session_mode: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-dark-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-dark-bg-secondary dark:text-dark-text-primary"
              required
            >
              <option value="available_online">Online Only</option>
              <option value="available_offline_online">Online & In-Person</option>
              <option value="available_offline">In-Person Only</option>
            </select>
            {isGoogleCalendarRequired && (
              <p className="bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200 text-xs px-2 py-1 rounded mt-2">
                Google Calendar connect is required for automatic video sessions creation as part of Online session booking by client
              </p>
            )}
          </div>
        </div>

        {/* Day-of-week checkboxes (Date Range mode only) */}
        {isRangeMode && (
          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-2">
              Days of Week <span className="text-red-500">*</span>
            </label>
            <div className="flex flex-wrap gap-2">
              {DAY_LABELS.map((label, index) => {
                const isSelected = (formData.selected_days || []).includes(index);
                return (
                  <button
                    key={index}
                    type="button"
                    onClick={() => handleDayToggle(index)}
                    className={`px-3 py-1.5 text-sm font-medium rounded-md border transition-colors ${
                      isSelected
                        ? 'bg-primary-600 text-white border-primary-600'
                        : 'bg-white dark:bg-dark-bg-secondary text-gray-700 dark:text-dark-text-secondary border-gray-300 dark:border-dark-border hover:bg-gray-50 dark:hover:bg-dark-bg-tertiary'
                    }`}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
            {formData.selected_days && formData.selected_days.length === 0 && (
              <p className="text-xs text-red-500 mt-1">Please select at least one day</p>
            )}
          </div>
        )}

        {/* Submit Button */}
        <div className="mt-6">
          <button
            type="submit"
            disabled={loading || availableTherapyTypes.length === 0 || !isFormValid}
            className={`w-full md:w-auto px-6 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 transition-colors ${
              loading || availableTherapyTypes.length === 0 || !isFormValid ? 'opacity-50 cursor-not-allowed' : ''
            }`}
          >
            {loading ? 'Creating...' : isRangeMode ? 'Create Slots' : 'Create Slot'}
          </button>

          {/* Validation message */}
          {!isFormValid && availableTherapyTypes.length > 0 && (
            <p className="mt-2 text-sm text-amber-600 dark:text-amber-400">
              {isGoogleCalendarRequired
                ? 'Please connect Google Calendar to create online availability slots'
                : 'Please fill in all required fields to enable the Create Slot button'}
            </p>
          )}
        </div>
      </form>

      {/* Online sessions info */}
      <div className="mt-4 p-3 bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-md">
        <p className="text-sm text-purple-800 dark:text-purple-300">
          <strong>Info:</strong> For Online sessions booking, Google Meet video sessions are automatically created in appointments calendar.
        </p>
      </div>

      {/* Info Text */}
      {availableTherapyTypes.length === 0 ? (
        <div className="mt-4 p-3 bg-yellow-50 dark:bg-dark-bg-tertiary border border-yellow-200 dark:border-yellow-800 rounded-md">
          <p className="text-sm text-yellow-800 dark:text-yellow-300">
            <strong>Note:</strong> Please set up at least one therapy type with fee and duration in the "Set Session Fee" section before creating slots.
          </p>
        </div>
      ) : (
        <div className="mt-4 p-3 bg-blue-50 dark:bg-dark-bg-tertiary border border-blue-200 dark:border-blue-800 rounded-md">
          <p className="text-sm text-blue-800 dark:text-blue-300">
            <strong>Note:</strong> Available slots will be checked for Google Calendar conflicts.
            You can still create the slot if there's a conflict. Slots won't be visible to clients until you publish them.
          </p>
        </div>
      )}
    </div>
  );
};

export default AvailabilitySlotForm;
